import { raygun } from '@ynap/clientside-monitoring'
import { addressValidation, toWCSMapping, validate as addressValidate } from '@ynap/address-form-v1'

const getToken = () => {
    const decodedToken = window.atob(window.applePayToken)
    const object = JSON.parse(decodedToken)
    return object
}

const returnToken = (object) => {
    const objectToString = JSON.stringify(object)
    const stringToBase64 = window.btoa(objectToString)

    return stringToBase64
}

export const checkIfUndefinedAsString = (currentAddress) =>
        currentAddress?.firstName === 'undefined' || currentAddress?.lastName === 'undefined' || currentAddress?.addressLine[0] === 'undefined'

export const getApplePayAddress = ({
    givenName,
    familyName,
    addressLines,
    locality,
    administrativeArea,
    postalCode,
    countryCode,
    phoneNumber,
    isTransient = true
}) => {
    const undefinedField = 'undefined'
    const newAddressFormat = {
        addressType: 'ShippingAndBilling',
        firstName: givenName || undefinedField,
        lastName: familyName || undefinedField,
        addressLine: addressLines || [undefinedField],
        city: locality,
        state: administrativeArea,
        zipCode: postalCode,
        country: countryCode,
        phone1: phoneNumber,
        isTransient,
        externalAddressValidation: 'false',
        fromBasket: true,
    }

    for (let key in newAddressFormat) {
        if (typeof newAddressFormat[key] == 'string') {
            const trimmedValue = newAddressFormat[key].trim()
            if (trimmedValue.length == 0) {
                delete newAddressFormat[key];
            } else {
                newAddressFormat[key] = trimmedValue
            }
        }
    }

    return newAddressFormat
}

export const startApplePaySession = (applePaySession, params) => {
    const { checkout, setCheckoutError, updatePaymentMethod, history, redirect, trackProceedToPurchaseApplePay, trackCancelApplePay, trackErrorApplePay } =
        params
    trackProceedToPurchaseApplePay()

    let applePayPopupOpen = true
    applePaySession.begin()

    applePaySession.onvalidatemerchant = (event) => {
        const paymentParams = {
            id: 'APPLEPAY',
            validationUrl: event.validationURL,
        }

        updatePaymentMethod(paymentParams)
            .then(() => checkout())
            .then(() => applePaySession.completeMerchantValidation(getToken()))
            .catch((error) => {
                if (applePayPopupOpen) {
                    trackErrorApplePay(error)
                    setCheckoutError(new Error('APPLEPAY_ERROR'))
                    applePaySession.abort()
                    applePayPopupOpen = false
                    raygun.helpers.agent('send', {
                        error: new Error('ApplePay merchant validation error'),
                        tags: ['APPLEPAY'],
                        customData: { error },
                    })
                } else {
                    setCheckoutError(new Error('APPLEPAY_CANCELLED'))
                }
            })
    }

    applePaySession.onpaymentauthorized = (event) => {
        const lastFourDigits = event.payment.token.paymentMethod.displayName.split(' ')[1]
        const token = returnToken(event.payment.token.paymentData)

        const paymentParams = {
            id: 'APPLEPAYUPDATE',
            token,
            lastFourDigits,
        }

        updatePaymentMethod(paymentParams)
            .then(() => checkout())
            .then((res) => {
                const { redirectType } = res || {}
                if (redirectType) {
                    throw new Error(`ApplePay payment failed because of redirect: ${redirectType}`)
                }
                applePayPopupOpen && applePaySession.completePayment(window?.ApplePaySession?.STATUS_SUCCESS)
            })
            .then(() => redirect && history.push(redirect))
            .catch((error) => {
                if (applePayPopupOpen) {
                    trackErrorApplePay(error)
                    setCheckoutError(new Error('APPLEPAY_ERROR'))
                    applePaySession.completePayment(window?.ApplePaySession?.STATUS_FAILURE)
                    applePayPopupOpen = false
                    raygun.helpers.agent('send', {
                        error: new Error('ApplePay authorization error'),
                        tags: ['APPLEPAY', error.message?.toLowerCase().includes('redirect') && 'APPLEPAY_REDIRECT_ERROR'],
                        customData: { error: error.message || error },
                    })
                } else {
                    setCheckoutError(new Error('APPLEPAY_CANCELLED'))
                }
            })
    }

    applePaySession.oncancel = () => {
        trackCancelApplePay()
        setCheckoutError(new Error('APPLEPAY_CANCELLED'))
        applePayPopupOpen = false
    }
}

export const startApplePayBasketSession = async (applePaySession, params = {}) => {
    const {
        applePayCheckout,
        finaleApplePayFlow,
        getApplePayRequestSb,
        initialApplePayFlow,
        history,
        orderLevelTaxes,
        redirect,
        setShippingAddress,
        setShippingMethod,
        setCheckoutError,
        trackCancelApplePay,
        updatePaymentMethod,
        createFastUser,
        config,
        messages,
        globalConfig,
        PCCC
    } = params
    let applePayPopupOpen = true
    applePaySession.begin()

    applePaySession.onvalidatemerchant = () => {
        initialApplePayFlow()
            .then(() => applePaySession.completeMerchantValidation(getToken()))
            .catch(() => {
                if (applePayPopupOpen) {
                    applePayPopupOpen = false
                }
            })
    }

    applePaySession.onshippingcontactselected = async (event) => {
        const paymentParams = {
            id: 'APPLEPAY',
            validationUrl: event.validationURL,
        }
        const partialAddress = getApplePayAddress(event.shippingContact)

        try {
            await setShippingAddress(partialAddress)
            await updatePaymentMethod(paymentParams)

            const { total, lineItems, shippingOptions } = getApplePayRequestSb?.({ type: 'basket', orderLevelTaxes }) || {}

            const completeShippingSelection = {
                newTotal: total,
                newLineItems: lineItems,
                newShippingMethods: shippingOptions?.map((el) => ({ ...el, amount: formatShippingOptionAmount(el.amount) })),
            }
            applePaySession.completeShippingContactSelection(completeShippingSelection)
        } catch (error) {
            const { total, lineItems } = getApplePayRequestSb?.({ type: 'basket', orderLevelTaxes }) || {}

            applePaySession.completeShippingContactSelection({
                newTotal: total,
                newLineItems: lineItems,
                errors: [new window.ApplePayError('addressUnserviceable', 'addressLines', 'Invalid Address')],
            })
        }
    }

    applePaySession.onshippingmethodselected = async (event) => {
        await setShippingMethod({
            addressId: '0',
            shippingOptionId: event.shippingMethod?.identifier,
            
        })
        const { total, lineItems, shippingOptions } = getApplePayRequestSb?.({ type: 'basket', orderLevelTaxes }) || {}

        applePaySession.completeShippingMethodSelection({
            status: window?.ApplePaySession?.STATUS_SUCCESS,
            newTotal: total,
            newLineItems: lineItems,
            newShippingMethods: shippingOptions?.map((el) => ({ ...el, amount: formatShippingOptionAmount(el.amount) })),
        })
    }

    applePaySession.onpaymentauthorized = async (event) => {
        const lastFourDigits = event.payment.token.paymentMethod.displayName.split(' ')[1]
        const token = returnToken(event.payment.token.paymentData)

        const paymentParams = {
            id: 'APPLEPAYUPDATE',
            token,
            lastFourDigits,
        }
        const fullAddress = getApplePayAddress(event.payment.shippingContact)
        const shippingAddress = await setShippingAddress(fullAddress)
        const email = event.payment.shippingContact.emailAddress

        isAddressValid({...fullAddress, PCCC }, fullAddress?.country, config, messages, globalConfig ).then(() => {
            createFastUser({ logonId: email, maMarketingConsent: 'false' })
                .then(async () => await finaleApplePayFlow(paymentParams, shippingAddress?.addressId, fullAddress))
                .then(async () => await applePayCheckout())
                .then(async (res) => {
                    const { redirectType, error } = res || {}
                    if (redirectType) {
                        throw new Error(`ApplePay payment failed because of redirect: ${redirectType}`)
                    }
                    if (error) {
                        throw new Error(error)
                    }
                    getToken()
                })
                .then(() => {
                    applePaySession.completePayment({ status: window?.ApplePaySession?.STATUS_SUCCESS })
                    redirect && history.push(redirect)
                })
                .catch(() => {
                    setCheckoutError(new Error('APPLEPAY_ERROR'))
                    applePaySession.completePayment({ status: window?.ApplePaySession?.STATUS_FAILURE })
                })
        }).catch(error => {
            applePaySession.completePayment({ status: window?.ApplePaySession?.STATUS_FAILURE, errors: error })
        })
    }
    applePaySession.oncancel = () => {
        trackCancelApplePay()
        setCheckoutError(new Error('APPLEPAY_CANCELLED'))
        if (applePayPopupOpen) {
            applePayPopupOpen = false
        }
    }
}

function formatShippingOptionAmount(amount) {
    if (amount === null) {
        return 0.0
    } else if (Array.isArray(amount)) {
        return amount[0]
    } else {
        return amount
    }
}

export async function isAddressValid(currentAddress, country, config, messages, globalConfig) {
    const newConfig= {
        head: {
            division: config.division
        },
        global: globalConfig
    }
    return new Promise( async (resolve, reject) => {
        const addressValidationOptions = await addressValidation({ countryCode: country, config: newConfig, countries: [] })
        const { fields } = addressValidationOptions[country] || {}
    
        if (!fields) {
            return false
        }
    
        const fieldsWCS = fields
            .filter((field) => field.type.toString().toLowerCase() != 'country')
            .map((field) => ({ ...field, wcsType: toWCSMapping(field.type) }))
    
        const shouldValidateNonLatin = !config?.head?.addressValidationOptions?.nonLatinValidationBlocklist?.includes(country)
    
        const validatedFields = fieldsWCS
            .map((field) => {
                const fieldValue = (field.wcsType.accessor ? field.wcsType.accessor(currentAddress) : currentAddress[field.wcsType.name]) || ''
                // allow province/state field to have non-latin characters for countries like Japan
                const errorMessages =  addressValidate(
                    messages,
                    field,
                    field.wcsType.name,
                    undefined,
                    shouldValidateNonLatin && field.type.toLowerCase() === 'province'
                )(fieldValue)
                return errorMessages.length > 0 ? new ApplePayError('shippingContactInvalid', fieldNameFormatter(field.wcsType.name), errorMessages[0]) : undefined
            }).filter(errors => errors !== undefined)
            
        if (validatedFields.length > 0) {
            reject(validatedFields)
        } else {
            resolve(true)
        }
    })
  
}

function fieldNameFormatter(field) {
    switch (field) {
        case 'phone1':
            return 'phoneNumber'
        case 'addressLine[0]':
            return 'addressLines'
        case 'lastname':
        case 'firstName':
            return 'name'
        case 'zipCode':
            return 'postalAddress'
        case 'state':
            return 'administrativeArea'
        default:
            return
    }

}
